import videojs from 'video.js';

import BannerLink from './banner/banner-link.js';
import { getDominantColor } from './banner/get-dominant-color.js';
import { generateEndedOverlay } from './banner/ended-modal-template.js';

import './banner.css';
import ModalDialog from '../../components/modal-dialog.js';
import Overlay, { OverlayPosition } from '../../components/overlay.js';

const _bannerLink = new WeakMap();
const _img = new WeakMap();
const _enabled = new WeakMap();

const _endedEnabled = new WeakMap();
const _endedModal = new WeakMap();
const _endedLink = new WeakMap();
const _endedImg = new WeakMap();
const _endedText = new WeakMap();
const _replayButton = new WeakMap();
let localize;

/**
 * The Banner module.
 * @member {BannerPlugin} banner
 * @memberof MediasitePlayer#
 */

/**
 * Configures a banner image and link that may be displayed at the top of the player area.<br><br>
 * Note: the banner image will only be displayed if {@link BannerPlugin#enabled} is set to <code>true</code>, and {@link BannerPlugin#src} and {@link BannerPlugin#href} have each been set to a non-empty string.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class BannerPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);
        _enabled.set(this, false);
        _endedEnabled.set(this, false);

        player.ready(() => {
            const bannerLink = new BannerLink(player);

            const img = document.createElement('img');

            img.setAttribute("alt", localize("Video banner"));
            bannerLink.el().appendChild(img);
            player.layout().addChild(bannerLink);

            _bannerLink.set(this, bannerLink);
            _img.set(this, img);

            this.width = 468;
            this.height = 60;

            this.enabled = false;

            const overlayContents = generateEndedOverlay(player);
            const endedLink = overlayContents.querySelector('a');
            _endedLink.set(this, endedLink);

            const endedImg = overlayContents.querySelector('img');
            _endedImg.set(this, endedImg);

            const endedText = overlayContents.querySelector('.vjs-presentation-ended-text');
            _endedText.set(this, endedText);

            const replayButton = overlayContents.querySelector('button');
            this.on(replayButton, 'click', this.#handleClickedReplay);
            _replayButton.set(this, replayButton);

            const endedOverlay = new Overlay(player, {
                text: '',
                position: OverlayPosition.Center
            });

            for (const childEl of Array.from(overlayContents.children)) {
                endedOverlay.el().appendChild(childEl);
            }

            const endedModal = new ModalDialog(player, {
                content: [endedOverlay.el()],
                solidBackground: false,
                uncloseable: true,
                temporary: false,
                label: localize('Presentation Ended')
            });
            endedModal.addClass('mediasite-player__presentation-ended-modal');
            player.layout().addChild(endedModal);
            _endedModal.set(this, endedModal);

            this.on(player, 'ended', this.#handlePresentationEnded);
            
        });
    }

    triggerResize() {
        this.player.trigger('playerresize');
    }

    updateLayout() {
        const bannerLink = _bannerLink.get(this);
        if (this.src && this.enabled) {
            bannerLink.removeClass('vjs-hidden');
        }
        else {
            bannerLink.addClass('vjs-hidden');
        }
        this.triggerResize();
    }

    /**
     * The path to an image to display.
     * @type {string}
     * @example
     * player.banner.src = '/path/to/image.jpg';
     */
    get src() {
        return _img.get(this)?.src;
    }

    set src(src) {
        this.player.ready(() => {
            _img.get(this).src = src;
            this.updateLayout();
        });
    }

    /**
     * A link to which the banner should point.
     * @type {string}
     * @example
     * player.banner.href = '/path/to/web/page/';
     */
    get href() {
        return _bannerLink.get(this).el().href;
    }

    set href(href) {
        this.player.ready(() => {
            const bannerEl = _bannerLink.get(this).el();
            if (!href?.length) {
                bannerEl.removeAttribute('href');
                bannerEl.setAttribute('tabindex', -1);
                bannerEl.setAttribute('aria-disabled', true);
            } else {
                bannerEl.href = href;
            }
            this.updateLayout();
        });
    }

    /**
     * A boolean indicating whether or not the Banner feature is enabled.
     * @type {boolean}
     * @example
     * player.banner.enabled = true;
     */
    get enabled() {
        return _enabled.get(this);
    }

    set enabled(enabled) {
        this.player.ready(() => {
            _enabled.set(this, enabled);
            this.updateLayout();
        });
    }

    /**
     * Configures how the banner is positioned in the layout.
     * @type {'Static'|'AutoHide'}
     * @example
     * player.banner.behavior = 'AutoHide';
     */
    get behavior() {
        return _bannerLink.get(this)?.behavior;
    }

    set behavior(behavior) {
        this.player.ready(() => {
            _bannerLink.get(this).behavior = behavior;
            this.updateLayout();
        });
        
    }

    /**
     * The desired display width of the banner.
     * @type {number}
     * @example
     * player.banner.width = 500;
     */
    get width() {
        return parseInt(_img.get(this).width, 10);
    }

    set width(width) {
        this.player.ready(() => {
            _img.get(this).width = width;
            this.triggerResize();
        });
    }

    /**
     * The desired display height of the banner.
     * @type {number}
     * @example
     * player.banner.height = 100;
     */
    get height() {
        return parseInt(_img.get(this).height, 10);
    }

    set height(height) {
        this.player.ready(() => {
            _img.get(this).height = height;
            this.triggerResize();
        });
    }

    /** 
     * The path to a banner image to display when the presentation has ended.
     * @type {string}
     * @example
     * player.banner.endedSrc = '/path/to/image.jpg';
     */
    get endedSrc() {
        return _endedImg.get(this)?.src;
    }

    set endedSrc(endedSrc) {
        this.player.ready(() => {
            const endedImg = _endedImg.get(this);
            const endedText = _endedText.get(this);
            if (endedSrc) {
                endedImg.src = endedSrc;
                endedImg.style.display = "block";
                endedText.style.display = "none";

                getDominantColor(endedSrc).then(color => {
                    const endedModal = _endedModal.get(this);
                    endedModal.el().style.setProperty('--ended-banner-bg-color', `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`);
                });
            }
            else {
                endedImg.removeAttribute('src');
                endedImg.style.display = "none";
                endedText.style.display = "block";
            }
        });
    }

    /**
     * A URL to use as a link for the Presentation Ended banner image, if present.
     * @type {string}
     * @example
     * player.banner.endedHref = '/path/to/web/page';
     */
    get endedHref() {
        return _endedLink.get(this)?.href;
    }

    set endedHref(endedHref) {
        this.player.ready(() => {
            const endedLink = _endedLink.get(this);
            if (endedHref) {
                endedLink.href = endedHref;
            }
            else {
                endedLink.removeAttribute('href');
            }
        });
    }

    /**
     * The desired display width of the Presentation Ended banner.
     * @type {number}
     * @example
     * player.banner.endedWidth = 500;
     */
    get endedWidth() {
        const endedImg = _endedImg.get(this);
        return endedImg ? parseInt(endedImg.width, 10) : 0;
    }

    set endedWidth(endedWidth) {
        this.player.ready(() => {
            const endedImg = _endedImg.get(this);
            endedImg.width = endedWidth;
            _endedModal.get(this).el().style.setProperty('--ended-banner-width', `${endedWidth}px`);
        });
    }

    /**
     * The desired display height of the Presentation Ended banner.
     * @type {number}
     * @example
     * player.banner.endedHeight = 100;
     */
    get endedHeight() {
        const endedImg = _endedImg.get(this);
        return endedImg ? parseInt(endedImg.height, 10) : 0;
    }

    set endedHeight(endedHeight) {
        this.player.ready(() => {
            const endedImg = _endedImg.get(this);
            endedImg.height = endedHeight;
        });
    }

    /**
     * Whether or not to show the "Presentation Ended" banner at the end of the presentation.
     * @type {boolean}
     * @example
     * player.banner.endedEnabled = true;
     */
    get endedEnabled() {
        return _endedEnabled.get(this);
    }

    set endedEnabled(endedEnabled) {
        _endedEnabled.set(this, endedEnabled);

        if (endedEnabled && this.player.currentTime() === this.player.duration()) {
            this.#handlePresentationEnded();
        }
        else {
            const endedModal = _endedModal.get(this);
            if (endedModal?.opened()) {
                endedModal.close();
            }
        }
        
    }

    #handlePresentationEnded() {
        if (this.player.error()) {
            //if it is a live presentation and there is an error, the live plugin will determine whether it is actually
            //the end of the presentation or an error, so we shouldn't display a false presentation ended message
            return
        }
        const endedModal = _endedModal.get(this);
        //if (this.endedEnabled && this.endedSrc && !endedModal.opened?.()) {
        if (this.endedEnabled && !endedModal.opened?.()) {
            const replayButton = _replayButton.get(this);
            if (this.player.usingPlugin('live')) {
                replayButton.classList.add('vjs-hidden');
            }
            else {
                replayButton.classList.remove('vjs-hidden');
            }

            endedModal.open();

            this.one(this.player, 'timeupdate', function () {
                endedModal.close();
            });
        }
    }

    #handleClickedReplay() {
        _endedModal.get(this)?.close();
        this.player.currentTime(0);
        this.player.one('timeupdate', () => {
            this.player.play();
        });
    }

    dispose() {
        const bannerLink = _bannerLink.get(this);
        const endedModal = _endedModal.get(this);
        this.player.layout().removeChild(bannerLink);
        this.player.layout().removeChild(endedModal);
    }
}

export default BannerPlugin;
